import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { useModal } from '@ebay/nice-modal-react'

import { Button, useSEOCountriesJSON } from '@pepper/ui'

import TargetStat from './TargetStat/TargetStat'
import TrafficStat from './TrafficStat/TrafficStat'
import ShareReport from './ShareReport/ShareReport'
import CreateItemDropdown from 'components/CreateItemDropdown'
import CreateItemModal from 'components/CreateItemModal/CreateItemModal'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { useShareGapReport } from 'container/SEOTools/hooks/useShareGapReport'
import type { GapReport, GapReportDocument } from '@pepper/utils/src/types/seo'

import s from './Metrics.module.css'

type Props = {
  document: GapReportDocument['document']
  report: GapReport
}

const Metrics = ({ document, report }: Props) => {
  const [link, setLink] = useState(report?.share_url ?? '')
  const [isOpen, setIsOpen] = useState(false)

  const isPublic = window.location.pathname.includes('content-refresh-report')

  const companyId = useSelectedCompanyId()
  const { data: countriesJson } = useSEOCountriesJSON()

  const createProjectModal = useModal(CreateItemModal)

  const { mutateAsync: shareReport, isLoading: isCreating } =
    useShareGapReport()

  const handleShare = async () => {
    const response = await shareReport({
      companyId,
      documentId: report.document_id,
      reportId: report.id,
    })
    if (response?.data) {
      setIsOpen(true)
      setLink(response.data?.share_url ?? '')
    }
  }

  const handleOrderContent = async (type: 'private_doc' | 'assignment') => {
    const DEFAULT_VERTICAL_ID = 20
    createProjectModal.show({
      items: [
        {
          title: report.page_title,
          content_gap_report_id: report.id,
          word_count: Math.floor(document.competitor_stats.length.mean),
        },
      ],
      type,
      title:
        type === 'assignment'
          ? 'Order from Expert Talent'
          : 'Create Item with PepperDocs',
      defaultValues: { verticalId: DEFAULT_VERTICAL_ID },
    })
  }

  return (
    <div className={s.wrapper}>
      <div className={s.headWrapper}>
        <div className={s.left}>
          <Link className={s.back} to="/content-refresh">
            <Button icon={<Icon icon="ph:arrow-left-bold" />} type="text" />
          </Link>

          <div className={s.header}>
            <div className={s.titleWrapper}>
              <h4 className={s.title}>
                {report.page_title ?? 'Untitled report'}
              </h4>

              <a
                className={s.link}
                href={report.page_url}
                title={report.page_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {report.page_url.replace(/(https:\/\/)|(www\.)/g, '')}
              </a>
            </div>
            <div
              className={s.flag}
              title={countriesJson?.[report.region_code].label}
            >
              <img
                alt={report.region_code}
                className={s.flagImg}
                height={16}
                width={16}
                src={countriesJson?.[report.region_code].flag}
              />
            </div>
          </div>
        </div>
        {!isPublic && (
          <div className={s.headerBtnContainer}>
            <Button
              type="default"
              iconPlacement="right"
              icon={<Icon icon="lucide:upload" />}
              onClick={!link ? () => handleShare() : () => setIsOpen(true)}
              loading={isCreating}
            >
              Share report
            </Button>
            {report.item_id ? (
              <Button
                type="link"
                iconPlacement="right"
                icon={<Icon icon="octicon:chevron-right-16" />}
                href={
                  report?.item_type === 'private_doc'
                    ? `/${companyId}/items/${report?.item_id}`
                    : `/${companyId}/document/${report?.item_id}`
                }
                size="middle"
              >
                View Item
              </Button>
            ) : (
              <CreateItemDropdown
                onCreateOrderClick={() => handleOrderContent('assignment')}
                onCreatePrivateDocClick={() =>
                  handleOrderContent('private_doc')
                }
              >
                <Button type="primary">Revamp Content</Button>
              </CreateItemDropdown>
            )}
          </div>
        )}
      </div>

      <div className={s.metrics}>
        <TargetStat document={document} report={report} />
        <TrafficStat document={document} report={report} />
      </div>
      {isOpen && <ShareReport onClose={() => setIsOpen(false)} link={link} />}
    </div>
  )
}

export default Metrics
